
/**
 * Select an unused word from word options
 */
export const selectUnusedWord = (wordOptions: any[], usedWordsArray: string[]) => {
  // Find the first word option that hasn't been used before
  const usedWordsLowercase = new Set(usedWordsArray.map(word => word.toLowerCase()));
  let validOptionCount = 0;
  let usedWordCount = 0;
  
  for (const option of wordOptions) {
    if (!option || typeof option !== 'object') {
      console.log(`Skipping invalid option: ${JSON.stringify(option)}`);
      continue;
    }
    
    const word = option.word;
    if (!word) {
      console.log(`Skipping option with missing word property: ${JSON.stringify(option)}`);
      continue;
    }
    
    validOptionCount++;
    
    // Check if this word has already been used (case-insensitive comparison)
    if (!usedWordsLowercase.has(word.toLowerCase())) {
      console.log(`Selected word "${word}" (not previously used)`);
      return option;
    }
    
    usedWordCount++;
    console.log(`Word "${word}" already used, trying next option...`);
  }
  
  // Improved logging to diagnose all-words-used fallback case
  if (validOptionCount > 0 && validOptionCount === usedWordCount) {
    console.warn(`All ${validOptionCount} suggested words have already been used. Using fallback word.`);
  }
  
  // If all options are used, return null (caller will use fallback)
  return null;
};

/**
 * Get fallback word when no unused words are available
 */
export const getFallbackWord = (usedWordsArray: string[], error?: Error | null) => {
  // Expanded list of meaningful fallback words
  const fallbackWords = [
    "connection", "bridge", "link", "relation", "alliance", "bond", "tie", 
    "association", "correlation", "partnership", "fusion", "junction", "nexus",
    "pathway", "channel", "medium", "network", "interface", "conduit", "route",
    "passage", "line", "thread", "chain", "union", "combination", "meeting",
    "liaison", "intersection", "overlap", "contact", "communication", "exchange",
    "transfer", "relay", "portal", "gateway", "access", "dialogue", "conversation",
    "interaction", "engagement", "rapport", "affiliation", "attachment", "binding",
    "coalition", "collaboration", "cooperation", "synergy", "interplay", "mesh",
    "weave", "integration", "crossover", "blending", "merger", "unification", "link"
  ];
  
  // Find a fallback word that hasn't been used (case-insensitive)
  const usedWordsLowercase = new Set(usedWordsArray.map(word => word.toLowerCase()));
  const availableFallback = fallbackWords.find(word => !usedWordsLowercase.has(word.toLowerCase()));
  
  // If all words have been used, create a completely unique fallback
  const uniqueWords = [
    "synapse", "bridge", "interlink", "nexpoint", "crosspath", 
    "joinflow", "connectome", "linknode", "pathbridge", "netjunction"
  ];
  
  const uniqueFallback = uniqueWords.find(word => !usedWordsLowercase.has(word.toLowerCase())) 
    || `junction${Math.floor(Math.random() * 1000)}`;
  
  const fallbackWord = availableFallback || uniqueFallback;
  
  // Improved logging with specific reason for fallback
  const fallbackReason = error 
    ? `AI service error: ${error.message}` 
    : "All suggested words have already been used";
  console.log(`Using fallback word "${fallbackWord}" because: ${fallbackReason}`);
  
  return { 
    word: fallbackWord,
    explanation: `This word represents a connection between ${usedWordsArray.length > 0 ? 'the previous concepts' : 'concepts'}. (Note: Using a fallback word because ${fallbackReason}.)`
  };
};
